<template>
  <ChatModal
    name="group-profile-matrix"
    :title="$t('chat.groupProfileTitle')"
    @before-open="beforeOpen"
  >
    <div v-if="currentRoom" slot="default">
      <div :class="$style.header">
        <BaseAvatar
          :size="40"
          :class="$style.avatar"
          :img-path="fullyUrlGroupAvatar"
          :name="formattedUserName(currentRoom.name)"
          v-viewer="{ navbar: false, rotatable: false, title: false }"
        />
        <div :class="$style.info">
          <div :class="$style.name">{{ formattedUserName(currentRoom.name) }}</div>
          <div :class="$style.meta">{{ $t('chat.members') }}: {{ roomMembers.length }}</div>
          <div v-if="currentRoom.topic" :class="$style.description">{{ currentRoom.topic }}</div>
        </div>
      </div>
      <div v-if="isSelfOwner" :class="$style.headerActions">
        <Fab @click.native="changeGroupName" :class="$style.headerActionBtn">
          <BaseIcon name="pencil-alt" />
        </Fab>
        <FileInput
          accept="image/*"
          @input="onFileChange"
        >
          <Fab tag="div" :class="$style.headerActionBtn">
            <BaseIcon name="camera" />
          </Fab>
        </FileInput>
      </div>
      <div :class="$style.section">
<!--        <div>-->
<!--          <span @click="addMembers" class="link">+ {{ $t('chat.addParticipantsButton') }}</span>-->
<!--        </div>-->
        <UserList
          v-if="roomMembers.length"
          mode="disable"
          :users="roomMembers"
          :chatId="activeChatId"
        >
          <template v-slot:avatar="{ user }">
            <BaseAvatar
              :size="40"
              :class="$style.avatar"
              :img-path="user.avatar_url"
              :name="formattedUserName(user.display_name)"
            />
          </template>
          <template v-slot:role="{ user }">
            {{ formattedUserName(user.display_name) }}
            <span v-if="isUserOwner(user.id)" :class="$style.role">({{ $t('chat.owner') }})</span>
          </template>
          <template v-slot:default="{ user }">
            <div>
              <span
                v-if="isUserMy(user.id)"
                :class="[$style.action, $style.red]"
                @click.stop="leaveChat(user)"
              >{{ $t('leaveChatTitle') }}</span>
              <span
                v-if="!isUserOwner(user.id) && isSelfOwner"
                :class="[$style.action, $style.red]"
                @click.stop="removeUser(user)"
              >{{ $t('chat.removeUser') }}</span>
            </div>
          </template>
        </UserList>
      </div>
    </div>
  </ChatModal>
</template>

<script>
import ChatModal from '@/components/modals/ChatModal'
import BaseAvatar from '@/components/BaseAvatar'
import Fab from '@/components/Fab'
import UserList from '@/components/UserList'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import FileInput from '@/components/FileInput'
import { eventTypes } from '@/helpers/variables'
import { formattedUserName } from '@/helpers'
import { parseRouterQueryParam } from '@/helpers/index'

export default {
  name: 'group-profile-matrix-popup',
  components: {
    ChatModal,
    BaseAvatar,
    UserList,
    FileInput,
    Fab
  },
  data () {
    return {
      members: []
    }
  },
  computed: {
    ...mapGetters({
      currentRoom: 'matrix/currentRoom',
      rooms: 'matrix/rooms',
      roomMembers: 'matrix/roomMembers'
    }),
    isSelfOwner () {
      const room = window.matrixSDK.getRoom(this.activeChatId)
      if (room) {
        const sender = room.currentState.getStateEvents(eventTypes.create, '').event.sender
        return sender === room.myUserId
      } else {
        return false
      }
    },
    activeChatId () {
      return parseRouterQueryParam(this.$route.query.id, true)
    },
    currentRoomFully () {
      return window.matrixSDK.getRoom(this.activeChatId)
    },
    fullyUrlGroupAvatar () {
      return window.matrixSDK.mxcUrlToHttp(this.currentRoom.mxcUrl)
    }
  },
  methods: {
    formattedUserName,
    beforeOpen () {
      this.getRoomTopic()
    },
    ...mapActions({
      editChatPhoto: 'entities/chats/editPhoto',
      removeParticipants: 'entities/participants/remove', // 'entities/chats/removeParticipants'
      changeModerator: 'entities/participants/changeModerator'
    }),
    ...mapMutations({
      replaceRoomTopic: 'matrix/REPLACE_ROOM_TOPIC',
      removeRoomMember: 'matrix/REMOVE_ROOM_MEMBER',
      replaceRoomAvatar: 'matrix/REPLACE_ROOM_AVATAR'
    }),
    addMembers () {
      this.$modal.show('add-users-to-chat', { chatId: this.activeChatId })
    },
    removeUser (user) {
      this.$modal.show('confirm', {
        content: this.$t('chat.removeUserConfirmation', { name: user.display_name }),
        onSubmit: async () => {
          await window.matrixSDK.kick(this.activeChatId, user.id)
          this.removeRoomMember({ id: user.id })
          this.$modal.hide('confirm')
        }
      })
    },
    async getRoomTopic () {
      const response = await window.matrixSDK.getStateEvent(this.activeChatId, eventTypes.topic, '')
      const { topic } = response
      this.replaceRoomTopic({
        id: this.activeChatId,
        topic
      })
    },
    isUserMy (userId) {
      return window.matrixSDK.credentials.userId === userId
    },
    isUserOwner (userId) {
      if (!this.currentRoom) return false
      const room = window.matrixSDK.getRoom(this.activeChatId)
      const sender = room.currentState.getStateEvents(eventTypes.create, '').event.sender
      return sender === userId
    },
    leaveChat () {
      this.$modal.show('delete-chat-matrix')
    },
    changeGroupName () {
      this.$modal.show('edit-group-matrix')
    },
    onFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 1,
        file,
        onDone: this.onImageCropped
      })
    },
    async onImageCropped (canvas) {
      await canvas.toBlob(async (blob) => {
        const file = new File([blob], 'chat_image.jpeg', { type: 'image/jpeg' })
        await this.updateRoomAvatar(file)
      }, 'image/jpeg', 0.8)
    },
    async updateRoomAvatar (file) {
      const uploadResponse = await window.matrixSDK.uploadContent(file, {
        rawResponse: false,
        type: file.type,
        onlyContentUri: false
      })
      const content = {
        url: uploadResponse.content_uri
      }
      await window.matrixSDK.sendStateEvent(
        this.activeChatId,
        eventTypes.avatar,
        content
      )

      this.replaceRoomAvatar({
        id: this.activeChatId,
        url: uploadResponse.content_uri
      })
    }
  }
}
</script>

<style lang="scss" module>
.header {
  background-color: $main-bg-colored;
  display: flex;
  padding: 0 20px 25px;
}
.headerActions {
  position: relative;
  top: -20px;
  display: flex;
  justify-content: flex-end;
  padding-right: 60px;
}
.headerActionBtn {
  margin-left: 24px;
}

.avatar {
  margin-right: 12px;
  flex-shrink: 0;
  :global(img) {
    cursor: pointer;
  }
}
.info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: $text-lightest;
}
.name {
  font-size: 16px;
  font-weight: 500;
}
.meta,
.description {
  font-weight: 300;
  margin-top: 2px;
}

.role {
  font-size: 12px;
  color: $text-mid;
  font-weight: 300;
}
.action {
  font-size: 12px;
  color: $text-success;
  &:hover {
    border-bottom: 1px solid $text-success;
  }

  &.red {
    color: $text-error;
    &:hover {
      border-color: $text-error;
    }
  }
}

.section {
  padding: 10px 10px 20px 82px;
}
</style>
